'use client';

import type {
  EmblaCarouselType,
  EmblaOptionsType,
  EmblaPluginType,
} from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { type HTMLAttributes, type ReactNode, useEffect } from 'react';

import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

export type EmblaCustomOptionsType = Omit<EmblaOptionsType, 'loop' | 'align'>;

export interface SlidersProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'hidden'
> {
  rootChildren?: ReactNode;
  className?: string;
  containerClassName?: string;
  children: ReactNode;
  align?: EmblaOptionsType['align'];
  loop?: EmblaOptionsType['loop'];
  emblaOptions?: EmblaCustomOptionsType;
  plugins?: EmblaPluginType[];
  hidden?: boolean;
  onEmblaInit?: (emblaApi: EmblaCarouselType) => void;
}

export const Sliders = ({
  rootChildren,
  className,
  containerClassName,
  children,
  loop = false,
  align = 'center',
  emblaOptions,
  plugins = [],
  hidden = true,
  onEmblaInit,
  ...props
}: SlidersProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { ...emblaOptions, loop, align },
    plugins
  );

  useEffect(() => {
    if (emblaApi && onEmblaInit) {
      onEmblaInit(emblaApi);
    }
  }, [emblaApi, onEmblaInit]);

  return (
    <div
      className={cn('outline-none', { 'overflow-hidden': hidden }, className)}
      ref={emblaRef}
      {...createDataAttributes({ test: 'sliders' })}
      {...props}
    >
      <div
        className={cn('flex', containerClassName)}
        {...createDataAttributes({ test: 'sliders-container' })}
      >
        {children}
      </div>
      {rootChildren}
    </div>
  );
};
